// Trading channel configuration for the chat currently selected in the
// sidebar's ChatListPane. Three states:
//   1. No chat selected            → "Select a chat" empty state
//   2. Selected chat not enabled   → "Enable trading for this chat" CTA
//   3. Selected chat enabled       → full per-channel config form
//
// The chat list itself lives in the sidebar (ChatListPane). This component
// only reads/writes the trading config for the one selected chat.
//
// Persists via `PUT /settings` with the updated `tradingChannels` array.

import type { TelegramChannelSummary } from "@shared/api/schemas/telegram";
import type {
	TradingChannelConfig,
	TradingChannelExecutableIntent,
	TradingChannelKind,
	TradingChannelSamePairTradePolicy,
	TradingChannelTakeProfitExecutionMode,
	TradingChannelTakeProfitPriceMode,
	TradingChannelTpSplitChildOverride,
	TradingChannelTpSplitNumericMode,
	TradingChannelTpSplitSizingMode,
} from "@shared/api/schemas/trading-channels";
import {
	DEFAULT_TRADING_CHANNEL_SAME_PAIR_TRADE_POLICY,
	DEFAULT_TRADING_CHANNEL_SL_TO_ENTRY_UPDATE_DETECTION,
	DEFAULT_TRADING_CHANNEL_TAKE_PROFIT_EXECUTION_MODE,
	DEFAULT_TRADING_CHANNEL_TAKE_PROFIT_PRICE_MODE,
	getTradingChannelMatchingMode,
	normalizeTradingChannelTakeProfitPriceSelection,
	TRADING_CHANNEL_EXECUTABLE_INTENT_DISPLAY,
	TRADING_CHANNEL_EXECUTABLE_INTENTS,
	TRADING_CHANNEL_SAME_PAIR_TRADE_POLICY_DISPLAY,
	TRADING_CHANNEL_SL_TO_ENTRY_ENTRY_PRICE_PERCENT_FALLBACK_BOUNDS,
	TRADING_CHANNEL_SL_TO_ENTRY_FAVORABLE_RISK_DISTANCE_PERCENT_BOUNDS,
	TRADING_CHANNEL_TAKE_PROFIT_EXECUTION_MODE_DISPLAY,
	TRADING_CHANNEL_TAKE_PROFIT_PRICE_MODE_DISPLAY,
	TRADING_CHANNEL_TP_SPLIT_NUMERIC_MODE_DISPLAY,
	TRADING_CHANNEL_TP_SPLIT_SIZING_MODE_DISPLAY,
} from "@shared/trading-channels";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError, httpClient } from "../../client/http-client";

interface TradingChannelsSectionProps {
	profileId: string;
	channels: TelegramChannelSummary[];
	telegramReady: boolean;
	selectedChatId: number | null;
	tradingChannels: TradingChannelConfig[];
	onTradingChannelsChange: (next: TradingChannelConfig[]) => void;
}

const primaryButtonClassName =
	"tg-btn-primary inline-flex h-9 items-center justify-center rounded-xl px-4 text-[0.85rem] font-semibold disabled:cursor-wait disabled:opacity-60";
const inputClassName =
	"tg-input tg-text-primary h-10 w-full rounded-[14px] px-3.5 text-[0.9rem]";

const DEFAULT_RISK_PCT = 1;
const DEFAULT_AUTO_SL_THRESHOLD = 50;
const DEFAULT_EMERGENCY_SL_PCT = 0.2;

const makeDefaultConfig = (
	channel: TelegramChannelSummary,
): TradingChannelConfig => ({
	chatId: channel.id,
	kind: "text",
	title: channel.title,
	sendToEa: true,
	percentageRiskPerTrade: DEFAULT_RISK_PCT,
	autoStopLossThreshold: DEFAULT_AUTO_SL_THRESHOLD,
	immediateEntryEmergencyStopLossPercent: DEFAULT_EMERGENCY_SL_PCT,
	allowImmediateEntryWithoutTakeProfit: true,
	breakevenAtTpLevel: null,
	takeProfitLevels: "25,55,85",
	takeProfitVolumePct: "20,25,55",
	marketEntryTolerancePercent: null,
	samePairTradePolicy: DEFAULT_TRADING_CHANNEL_SAME_PAIR_TRADE_POLICY,
	ignoredExecutionIntents: [],
	slToEntryUpdateDetection: {
		...DEFAULT_TRADING_CHANNEL_SL_TO_ENTRY_UPDATE_DETECTION,
	},
	takeProfitExecutionMode: DEFAULT_TRADING_CHANNEL_TAKE_PROFIT_EXECUTION_MODE,
	takeProfitSplitChildOverrides: [],
	takeProfitPriceMode: DEFAULT_TRADING_CHANNEL_TAKE_PROFIT_PRICE_MODE,
	takeProfitPriceSelection: null,
});

const makeTpSplitOverride = (
	tpNumber: number,
): TradingChannelTpSplitChildOverride => ({
	tpNumber,
	sizingMode: "inherit",
	fixedRiskAmountPerTrade: null,
	percentageRiskPerTrade: null,
	lotSize: null,
	autoStopLossMode: "inherit",
	autoStopLossThreshold: null,
	marketEntryToleranceMode: "inherit",
	marketEntryTolerancePercent: null,
});

const parseOptionalNumber = (raw: string): number | null => {
	if (raw === "") return null;
	const value = Number.parseFloat(raw);
	return Number.isFinite(value) ? value : null;
};

const clamp = (value: number, bounds: { min: number; max: number }): number =>
	Math.min(Math.max(value, bounds.min), bounds.max);

export const TradingChannelsSection = ({
	profileId,
	channels,
	telegramReady,
	selectedChatId,
	tradingChannels,
	onTradingChannelsChange,
}: TradingChannelsSectionProps) => {
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const selectedChannel = useMemo(
		() =>
			selectedChatId === null
				? null
				: (channels.find((c) => c.id === selectedChatId) ?? null),
		[channels, selectedChatId],
	);

	const selectedConfig = useMemo(() => {
		if (selectedChatId === null) return null;
		return tradingChannels.find((c) => c.chatId === selectedChatId) ?? null;
	}, [selectedChatId, tradingChannels]);

	const persist = useCallback(
		async (nextConfigs: TradingChannelConfig[]) => {
			setSaving(true);
			setError(null);
			try {
				await httpClient.settings.save(profileId, {
					tradingChannels: nextConfigs,
				});
			} catch (err) {
				setError(err instanceof ApiError ? err.message : String(err));
			} finally {
				setSaving(false);
			}
		},
		[profileId],
	);

	const handleEnable = useCallback(() => {
		if (!selectedChannel) return;
		const next = makeDefaultConfig(selectedChannel);
		const merged = [
			...tradingChannels.filter((c) => c.chatId !== next.chatId),
			next,
		];
		onTradingChannelsChange(merged);
		void persist(merged);
	}, [selectedChannel, tradingChannels, onTradingChannelsChange, persist]);

	const handleDisable = useCallback(() => {
		if (selectedChatId === null) return;
		const merged = tradingChannels.filter((c) => c.chatId !== selectedChatId);
		onTradingChannelsChange(merged);
		void persist(merged);
	}, [selectedChatId, tradingChannels, onTradingChannelsChange, persist]);

	const patchConfig = useCallback(
		(patch: Partial<TradingChannelConfig>) => {
			if (!selectedConfig) return;
			const next = { ...selectedConfig, ...patch };
			const merged = tradingChannels.map((c) =>
				c.chatId === next.chatId ? next : c,
			);
			onTradingChannelsChange(merged);
			void persist(merged);
		},
		[selectedConfig, tradingChannels, onTradingChannelsChange, persist],
	);

	// Reset transient errors when selection changes.
	useEffect(() => {
		setError(null);
	}, [selectedChatId]);

	return (
		<section className="tg-surface rounded-2xl">
			<header className="border-b border-[color:var(--color-tg-line)] px-4 py-3">
				<h3 className="tg-text-primary text-[0.95rem] font-semibold leading-5">
					Trading channels
				</h3>
				<p className="tg-text-muted mt-0.5 text-[0.82rem] leading-5">
					{telegramReady
						? `${tradingChannels.length} configured for trading · ${channels.length} chats available`
						: "Sign in to Telegram above to start configuring trading channels."}
				</p>
			</header>

			{error ? (
				<div className="tg-alert-error mx-4 mt-3 rounded-xl px-3 py-2 text-[0.85rem]">
					{error}
				</div>
			) : null}

			<div className="px-5 py-6">
				{!selectedChannel ? (
					<div className="tg-text-muted grid place-items-center px-6 py-10 text-center text-[0.9rem] leading-6">
						{telegramReady
							? "Select a chat from the sidebar to configure trading for it."
							: "Sign in to Telegram on the right to load your chats."}
					</div>
				) : !selectedConfig ? (
					<div className="grid place-items-center py-8 text-center">
						<div className="max-w-[420px] space-y-3">
							<h4 className="tg-text-primary text-[1.05rem] font-semibold">
								{selectedChannel.title}
							</h4>
							<p className="tg-text-muted text-[0.88rem] leading-6">
								This chat isn’t configured for trading yet. Enable it to start
								parsing messages here into signals.
							</p>
							<button
								type="button"
								onClick={handleEnable}
								className={primaryButtonClassName}
								disabled={saving}
							>
								{saving ? "Enabling…" : "Enable trading for this chat"}
							</button>
						</div>
					</div>
				) : (
					<ChannelConfigForm
						channel={selectedChannel}
						config={selectedConfig}
						onDisable={handleDisable}
						onPatch={patchConfig}
						saving={saving}
					/>
				)}
			</div>
		</section>
	);
};

interface ChannelConfigFormProps {
	channel: TelegramChannelSummary;
	config: TradingChannelConfig;
	saving: boolean;
	onDisable: () => void;
	onPatch: (patch: Partial<TradingChannelConfig>) => void;
}

const ChannelConfigForm = ({
	channel,
	config,
	saving,
	onDisable,
	onPatch,
}: ChannelConfigFormProps) => {
	const matchingMode = getTradingChannelMatchingMode(config);
	const samePairPolicy =
		config.samePairTradePolicy ??
		DEFAULT_TRADING_CHANNEL_SAME_PAIR_TRADE_POLICY;
	const ignoredIntents = config.ignoredExecutionIntents ?? [];
	const slToEntry =
		config.slToEntryUpdateDetection ??
		DEFAULT_TRADING_CHANNEL_SL_TO_ENTRY_UPDATE_DETECTION;
	const tpExecutionMode =
		config.takeProfitExecutionMode ??
		DEFAULT_TRADING_CHANNEL_TAKE_PROFIT_EXECUTION_MODE;
	const tpPriceMode =
		config.takeProfitPriceMode ??
		DEFAULT_TRADING_CHANNEL_TAKE_PROFIT_PRICE_MODE;
	const tpOverrides = config.takeProfitSplitChildOverrides ?? [];

	// Draft for the TP-selection text input: normalized only on blur so typing
	// intermediate states like "2," isn't rewritten mid-keystroke.
	const [tpSelectionDraft, setTpSelectionDraft] = useState(
		config.takeProfitPriceSelection ?? "",
	);
	useEffect(() => {
		setTpSelectionDraft(config.takeProfitPriceSelection ?? "");
	}, [config.takeProfitPriceSelection]);

	const toggleIgnoredIntent = (
		intent: TradingChannelExecutableIntent,
		ignored: boolean,
	) => {
		const next = ignored
			? [...ignoredIntents, intent]
			: ignoredIntents.filter((i) => i !== intent);
		onPatch({
			// Canonical order + dedupe, matching the backend normalizer.
			ignoredExecutionIntents: TRADING_CHANNEL_EXECUTABLE_INTENTS.filter((i) =>
				next.includes(i),
			),
		});
	};

	return (
		<div className="grid gap-4">
			<div className="flex items-start justify-between gap-3">
				<div>
					<h4 className="tg-text-primary text-[1.05rem] font-semibold">
						{channel.title}
					</h4>
					<p className="tg-text-muted mt-0.5 text-[0.82rem]">
						chatId {channel.id}
					</p>
				</div>
				<button
					type="button"
					onClick={onDisable}
					className="tg-btn-danger inline-flex h-9 items-center rounded-xl px-3 text-[0.82rem] font-semibold"
					disabled={saving}
				>
					Disable
				</button>
			</div>

			<div className="grid gap-3">
				<label className="grid gap-1.5">
					<span className="tg-text-primary text-[0.86rem] font-semibold">
						Channel kind
					</span>
					<select
						className="tg-select tg-text-primary h-10 rounded-[14px] px-3 text-[0.9rem]"
						value={config.kind}
						onChange={(e) =>
							onPatch({ kind: e.target.value as TradingChannelKind })
						}
						disabled={saving}
					>
						<option value="text">Message-linked (text)</option>
						<option value="image">Pair-linked (image)</option>
					</select>
					<span className="tg-text-muted text-[0.78rem] leading-5">
						How follow-up messages are matched to an existing trade. Text
						channels use reply-thread tracking; image channels match the trading
						pair within the chat.
					</span>
				</label>

				{matchingMode === "pair-linked" && (
					<label className="grid gap-1.5">
						<span className="tg-text-primary text-[0.86rem] font-semibold">
							Same-pair trade policy
						</span>
						<select
							className="tg-select tg-text-primary h-10 rounded-[14px] px-3 text-[0.9rem]"
							value={samePairPolicy}
							onChange={(e) =>
								onPatch({
									samePairTradePolicy: e.target
										.value as TradingChannelSamePairTradePolicy,
								})
							}
							disabled={saving}
						>
							{Object.entries(
								TRADING_CHANNEL_SAME_PAIR_TRADE_POLICY_DISPLAY,
							).map(([value, { label }]) => (
								<option key={value} value={value}>
									{label}
								</option>
							))}
						</select>
						<span className="tg-text-muted text-[0.78rem] leading-5">
							{
								TRADING_CHANNEL_SAME_PAIR_TRADE_POLICY_DISPLAY[samePairPolicy]
									.description
							}
						</span>
					</label>
				)}

				<label className="flex items-center justify-between gap-3 rounded-xl border border-[color:var(--color-tg-line)] px-3 py-2.5">
					<span className="grid gap-1">
						<span className="tg-text-primary text-[0.88rem] font-semibold">
							Send to MT5 EA
						</span>
						<span className="tg-text-muted text-[0.78rem] leading-5">
							When off, signals are parsed + stored but never dispatched.
						</span>
					</span>
					<span className="relative inline-flex h-6 w-11 shrink-0">
						<input
							type="checkbox"
							className="peer sr-only"
							checked={config.sendToEa}
							onChange={(e) => onPatch({ sendToEa: e.target.checked })}
							disabled={saving}
						/>
						<span className="tg-switch-track absolute inset-0 rounded-full transition" />
						<span
							className={`tg-switch-thumb absolute top-1 h-4 w-4 rounded-full transition ${config.sendToEa ? "left-6" : "left-1"}`}
						/>
					</span>
				</label>

				<div className="grid grid-cols-2 gap-3">
					<label className="grid gap-1.5">
						<span className="tg-text-primary text-[0.86rem] font-semibold">
							Risk per trade (%)
						</span>
						<input
							className={inputClassName}
							type="number"
							step="0.05"
							min="0"
							value={config.percentageRiskPerTrade}
							onChange={(e) =>
								onPatch({
									percentageRiskPerTrade:
										Number.parseFloat(e.target.value) || 0,
								})
							}
							disabled={saving}
						/>
					</label>

					<label className="grid gap-1.5">
						<span className="tg-text-primary text-[0.86rem] font-semibold">
							Auto-SL threshold (%)
						</span>
						<input
							className={inputClassName}
							type="number"
							step="1"
							min="0"
							value={config.autoStopLossThreshold ?? ""}
							onChange={(e) =>
								onPatch({
									autoStopLossThreshold:
										e.target.value === ""
											? null
											: Number.parseFloat(e.target.value),
								})
							}
							placeholder="(none)"
							disabled={saving}
						/>
					</label>
				</div>

				<div className="grid grid-cols-2 gap-3">
					<label className="grid gap-1.5">
						<span className="tg-text-primary text-[0.86rem] font-semibold">
							Take-profit levels (%)
						</span>
						<input
							className={inputClassName}
							type="text"
							value={config.takeProfitLevels ?? ""}
							placeholder="25,55,85"
							onChange={(e) =>
								onPatch({ takeProfitLevels: e.target.value || null })
							}
							disabled={saving}
						/>
					</label>

					<label className="grid gap-1.5">
						<span className="tg-text-primary text-[0.86rem] font-semibold">
							TP volume split (%)
						</span>
						<input
							className={inputClassName}
							type="text"
							value={config.takeProfitVolumePct ?? ""}
							placeholder="20,25,55"
							onChange={(e) =>
								onPatch({ takeProfitVolumePct: e.target.value || null })
							}
							disabled={saving}
						/>
					</label>
				</div>

				<label className="grid gap-1.5">
					<span className="tg-text-primary text-[0.86rem] font-semibold">
						Market entry tolerance (%)
					</span>
					<input
						className={inputClassName}
						type="number"
						step="0.01"
						min="0"
						value={config.marketEntryTolerancePercent ?? ""}
						placeholder="(none = always limit)"
						onChange={(e) =>
							onPatch({
								marketEntryTolerancePercent:
									e.target.value === ""
										? null
										: Number.parseFloat(e.target.value),
							})
						}
						disabled={saving}
					/>
					<span className="tg-text-muted text-[0.78rem] leading-5">
						If live price is within this % of the signal entry, the EA fires a
						market order instead of a pending limit.
					</span>
				</label>

				<details className="tg-surface-soft rounded-xl p-3 text-[0.85rem]">
					<summary className="tg-text-primary cursor-pointer font-semibold">
						Take-profit execution
					</summary>
					<div className="mt-3 grid gap-3">
						<label className="grid gap-1.5">
							<span className="tg-text-primary text-[0.84rem] font-semibold">
								Execution mode
							</span>
							<select
								className="tg-select tg-text-primary h-10 rounded-[14px] px-3 text-[0.85rem]"
								value={tpExecutionMode}
								onChange={(e) =>
									onPatch({
										takeProfitExecutionMode: e.target
											.value as TradingChannelTakeProfitExecutionMode,
									})
								}
								disabled={saving}
							>
								{Object.entries(
									TRADING_CHANNEL_TAKE_PROFIT_EXECUTION_MODE_DISPLAY,
								).map(([value, { label }]) => (
									<option key={value} value={value}>
										{label}
									</option>
								))}
							</select>
							<span className="tg-text-muted text-[0.78rem] leading-5">
								{
									TRADING_CHANNEL_TAKE_PROFIT_EXECUTION_MODE_DISPLAY[
										tpExecutionMode
									].description
								}
							</span>
						</label>

						<div className="grid grid-cols-2 gap-3">
							<label className="grid gap-1.5">
								<span className="tg-text-primary text-[0.84rem] font-semibold">
									TP price mode
								</span>
								<select
									className="tg-select tg-text-primary h-10 rounded-[14px] px-3 text-[0.85rem]"
									value={tpPriceMode}
									onChange={(e) =>
										onPatch({
											takeProfitPriceMode: e.target
												.value as TradingChannelTakeProfitPriceMode,
										})
									}
									disabled={saving}
								>
									{Object.entries(
										TRADING_CHANNEL_TAKE_PROFIT_PRICE_MODE_DISPLAY,
									).map(([value, { label }]) => (
										<option key={value} value={value}>
											{label}
										</option>
									))}
								</select>
							</label>

							<label className="grid gap-1.5">
								<span className="tg-text-primary text-[0.84rem] font-semibold">
									TP numbers
								</span>
								<input
									className={inputClassName}
									type="text"
									placeholder="2,3"
									value={tpSelectionDraft}
									onChange={(e) => setTpSelectionDraft(e.target.value)}
									onBlur={() =>
										onPatch({
											takeProfitPriceSelection:
												normalizeTradingChannelTakeProfitPriceSelection(
													tpSelectionDraft,
												),
										})
									}
									disabled={saving || tpPriceMode !== "selected"}
								/>
							</label>
						</div>
						<span className="tg-text-muted text-[0.78rem] leading-5">
							{
								TRADING_CHANNEL_TAKE_PROFIT_PRICE_MODE_DISPLAY[tpPriceMode]
									.description
							}
							{tpPriceMode === "selected"
								? " Enter comma-separated TP numbers, e.g. 2,3."
								: ""}
						</span>

						{tpExecutionMode === "separate-trades-per-tp" && (
							<>
								<div className="tg-alert-error rounded-xl px-3 py-2 text-[0.8rem]">
									Risk settings apply to each TP child trade. The staged TP
									ladder is not used in this mode.
								</div>
								<TpSplitOverridesEditor
									overrides={tpOverrides}
									saving={saving}
									onChange={(next) =>
										onPatch({ takeProfitSplitChildOverrides: next })
									}
								/>
							</>
						)}
					</div>
				</details>

				<details className="tg-surface-soft rounded-xl p-3 text-[0.85rem]">
					<summary className="tg-text-primary cursor-pointer font-semibold">
						Ignored actions
					</summary>
					<div className="mt-3 grid gap-3">
						<span className="tg-text-muted text-[0.78rem] leading-5">
							Checked actions are parsed and audited but never executed for this
							chat.
						</span>
						<div className="grid gap-2">
							{TRADING_CHANNEL_EXECUTABLE_INTENTS.map((intent) => (
								<label
									key={intent}
									className="flex items-start gap-2.5 rounded-xl border border-[color:var(--color-tg-line)] px-3 py-2"
								>
									<input
										type="checkbox"
										className="mt-1"
										checked={ignoredIntents.includes(intent)}
										onChange={(e) =>
											toggleIgnoredIntent(intent, e.target.checked)
										}
										disabled={saving}
									/>
									<span className="grid gap-0.5">
										<span className="tg-text-primary text-[0.84rem] font-semibold">
											{TRADING_CHANNEL_EXECUTABLE_INTENT_DISPLAY[intent].label}
										</span>
										<span className="tg-text-muted text-[0.76rem] leading-5">
											{
												TRADING_CHANNEL_EXECUTABLE_INTENT_DISPLAY[intent]
													.description
											}
										</span>
									</span>
								</label>
							))}
						</div>

						<label className="flex items-center justify-between gap-3 rounded-xl border border-[color:var(--color-tg-line)] px-3 py-2.5">
							<span className="grid gap-0.5">
								<span className="tg-text-primary text-[0.85rem] font-semibold">
									Detect entry-like SL updates as breakeven
								</span>
								<span className="tg-text-muted text-[0.76rem] leading-5">
									Treat an SL moved near the entry price as a "move SL to entry"
									instruction instead of a plain SL update.
								</span>
							</span>
							<span className="relative inline-flex h-5 w-10 shrink-0">
								<input
									type="checkbox"
									className="peer sr-only"
									checked={slToEntry.enabled}
									onChange={(e) =>
										onPatch({
											slToEntryUpdateDetection: {
												...slToEntry,
												enabled: e.target.checked,
											},
										})
									}
									disabled={saving}
								/>
								<span className="tg-switch-track absolute inset-0 rounded-full transition" />
								<span
									className={`tg-switch-thumb absolute top-0.5 h-4 w-4 rounded-full transition ${slToEntry.enabled ? "left-[22px]" : "left-0.5"}`}
								/>
							</span>
						</label>

						<div className="grid grid-cols-2 gap-3">
							<label className="grid gap-1.5">
								<span className="tg-text-primary text-[0.84rem] font-semibold">
									Risk band (%)
								</span>
								<input
									className={inputClassName}
									type="number"
									step="0.5"
									min={
										TRADING_CHANNEL_SL_TO_ENTRY_FAVORABLE_RISK_DISTANCE_PERCENT_BOUNDS.min
									}
									max={
										TRADING_CHANNEL_SL_TO_ENTRY_FAVORABLE_RISK_DISTANCE_PERCENT_BOUNDS.max
									}
									value={slToEntry.favorableRiskDistancePercent}
									onChange={(e) =>
										onPatch({
											slToEntryUpdateDetection: {
												...slToEntry,
												favorableRiskDistancePercent: clamp(
													Number.parseFloat(e.target.value) || 0,
													TRADING_CHANNEL_SL_TO_ENTRY_FAVORABLE_RISK_DISTANCE_PERCENT_BOUNDS,
												),
											},
										})
									}
									disabled={saving || !slToEntry.enabled}
								/>
							</label>
							<label className="grid gap-1.5">
								<span className="tg-text-primary text-[0.84rem] font-semibold">
									Entry band (%)
								</span>
								<input
									className={inputClassName}
									type="number"
									step="0.05"
									min={
										TRADING_CHANNEL_SL_TO_ENTRY_ENTRY_PRICE_PERCENT_FALLBACK_BOUNDS.min
									}
									max={
										TRADING_CHANNEL_SL_TO_ENTRY_ENTRY_PRICE_PERCENT_FALLBACK_BOUNDS.max
									}
									value={slToEntry.entryPricePercentFallback}
									onChange={(e) =>
										onPatch({
											slToEntryUpdateDetection: {
												...slToEntry,
												entryPricePercentFallback: clamp(
													Number.parseFloat(e.target.value) || 0,
													TRADING_CHANNEL_SL_TO_ENTRY_ENTRY_PRICE_PERCENT_FALLBACK_BOUNDS,
												),
											},
										})
									}
									disabled={saving || !slToEntry.enabled}
								/>
							</label>
						</div>
						<span className="tg-text-muted text-[0.76rem] leading-5">
							Risk band: SL within this % of the entry→SL distance counts as
							breakeven. Entry band: fallback % of the entry price when the
							original SL is unknown.
						</span>
					</div>
				</details>

				<details className="tg-surface-soft rounded-xl p-3 text-[0.85rem]">
					<summary className="tg-text-primary cursor-pointer font-semibold">
						Advanced
					</summary>
					<div className="mt-3 grid gap-3">
						<div className="grid grid-cols-2 gap-3">
							<label className="grid gap-1.5">
								<span className="tg-text-primary text-[0.84rem] font-semibold">
									Emergency SL (%)
								</span>
								<input
									className={inputClassName}
									type="number"
									step="0.05"
									min="0"
									value={config.immediateEntryEmergencyStopLossPercent}
									onChange={(e) =>
										onPatch({
											immediateEntryEmergencyStopLossPercent:
												Number.parseFloat(e.target.value) || 0,
										})
									}
									disabled={saving}
								/>
							</label>
							<label className="grid gap-1.5">
								<span className="tg-text-primary text-[0.84rem] font-semibold">
									Breakeven at TP
								</span>
								<select
									className="tg-select tg-text-primary h-10 rounded-[14px] px-3 text-[0.85rem]"
									value={config.breakevenAtTpLevel ?? ""}
									onChange={(e) =>
										onPatch({
											breakevenAtTpLevel:
												e.target.value === ""
													? null
													: (Number.parseInt(e.target.value, 10) as
															| 1
															| 2
															| 3
															| 4),
										})
									}
									disabled={saving}
								>
									<option value="">Never</option>
									<option value="1">After TP1</option>
									<option value="2">After TP2</option>
									<option value="3">After TP3</option>
									<option value="4">After TP4</option>
								</select>
							</label>
						</div>

						<label className="flex items-center justify-between gap-3 rounded-xl border border-[color:var(--color-tg-line)] px-3 py-2.5">
							<span className="grid gap-0.5">
								<span className="tg-text-primary text-[0.85rem] font-semibold">
									Allow immediate entry without TP
								</span>
								<span className="tg-text-muted text-[0.76rem] leading-5">
									If off, the EA waits for a TP level before opening a market
									trade from an immediate-propagation signal.
								</span>
							</span>
							<span className="relative inline-flex h-5 w-10 shrink-0">
								<input
									type="checkbox"
									className="peer sr-only"
									checked={config.allowImmediateEntryWithoutTakeProfit}
									onChange={(e) =>
										onPatch({
											allowImmediateEntryWithoutTakeProfit: e.target.checked,
										})
									}
									disabled={saving}
								/>
								<span className="tg-switch-track absolute inset-0 rounded-full transition" />
								<span
									className={`tg-switch-thumb absolute top-0.5 h-4 w-4 rounded-full transition ${config.allowImmediateEntryWithoutTakeProfit ? "left-[22px]" : "left-0.5"}`}
								/>
							</span>
						</label>

						<label className="grid gap-1.5">
							<span className="tg-text-primary text-[0.84rem] font-semibold">
								Pair filter mode
							</span>
							<select
								className="tg-select tg-text-primary h-10 rounded-[14px] px-3 text-[0.85rem]"
								value={config.pairFilterMode ?? "off"}
								onChange={(e) =>
									onPatch({
										pairFilterMode: e.target.value as
											| "off"
											| "include"
											| "exclude",
									})
								}
								disabled={saving}
							>
								<option value="off">Off — trade every pair</option>
								<option value="include">Include only — allowlist</option>
								<option value="exclude">Exclude — denylist</option>
							</select>
						</label>

						{(config.pairFilterMode === "include" ||
							config.pairFilterMode === "exclude") && (
							<label className="grid gap-1.5">
								<span className="tg-text-primary text-[0.84rem] font-semibold">
									{config.pairFilterMode === "include"
										? "Include pairs"
										: "Exclude pairs"}
								</span>
								<input
									className={inputClassName}
									placeholder="EURUSD,GBPUSD,XAUUSD"
									value={
										(config.pairFilterMode === "include"
											? config.pairIncludeList
											: config.pairExcludeList
										)?.join(",") ?? ""
									}
									onChange={(e) => {
										const list = e.target.value
											.split(",")
											.map((s) => s.trim().toUpperCase())
											.filter(Boolean);
										if (config.pairFilterMode === "include") {
											onPatch({
												pairIncludeList: list.length > 0 ? list : null,
											});
										} else {
											onPatch({
												pairExcludeList: list.length > 0 ? list : null,
											});
										}
									}}
									disabled={saving}
								/>
							</label>
						)}

						<label className="grid gap-1.5">
							<span className="tg-text-primary text-[0.84rem] font-semibold">
								Parser routing mode
							</span>
							<select
								className="tg-select tg-text-primary h-10 rounded-[14px] px-3 text-[0.85rem]"
								value={config.parserRoutingMode ?? "llm-only"}
								onChange={(e) =>
									onPatch({
										parserRoutingMode: e.target.value as
											| "llm-only"
											| "parser-enabled",
									})
								}
								disabled={saving}
							>
								<option value="llm-only">LLM only</option>
								<option value="parser-enabled">
									Parser when confident, LLM fallback
								</option>
							</select>
						</label>
					</div>
				</details>
			</div>
		</div>
	);
};

interface TpSplitOverridesEditorProps {
	overrides: TradingChannelTpSplitChildOverride[];
	saving: boolean;
	onChange: (next: TradingChannelTpSplitChildOverride[]) => void;
}

const smallInputClassName =
	"tg-input tg-text-primary h-9 w-full rounded-[12px] px-3 text-[0.82rem]";
const smallSelectClassName =
	"tg-select tg-text-primary h-9 rounded-[12px] px-2.5 text-[0.82rem]";

const TpSplitOverridesEditor = ({
	overrides,
	saving,
	onChange,
}: TpSplitOverridesEditorProps) => {
	const patchRow = (
		index: number,
		patch: Partial<TradingChannelTpSplitChildOverride>,
	) => {
		onChange(overrides.map((o, i) => (i === index ? { ...o, ...patch } : o)));
	};

	return (
		<div className="grid gap-2.5">
			<div className="flex items-center justify-between">
				<span className="tg-text-primary text-[0.84rem] font-semibold">
					Per-TP child overrides
				</span>
				<button
					type="button"
					className="tg-btn-primary inline-flex h-8 items-center rounded-lg px-3 text-[0.78rem] font-semibold disabled:cursor-wait disabled:opacity-60"
					onClick={() =>
						onChange([...overrides, makeTpSplitOverride(overrides.length + 1)])
					}
					disabled={saving}
				>
					Add TP
				</button>
			</div>
			{overrides.length === 0 ? (
				<span className="tg-text-muted text-[0.78rem] leading-5">
					No overrides — every TP child inherits the channel-level risk, SL, and
					tolerance settings.
				</span>
			) : (
				overrides.map((override, index) => (
					<div
						// biome-ignore lint/suspicious/noArrayIndexKey: rows are identified by position; tpNumber is user-editable and may repeat mid-edit
						key={index}
						className="grid gap-2.5 rounded-xl border border-[color:var(--color-tg-line)] p-3"
					>
						<div className="flex items-end justify-between gap-3">
							<label className="grid w-24 gap-1">
								<span className="tg-text-primary text-[0.78rem] font-semibold">
									TP number
								</span>
								<input
									className={smallInputClassName}
									type="number"
									min="1"
									step="1"
									value={override.tpNumber}
									onChange={(e) =>
										patchRow(index, {
											tpNumber: Math.max(
												1,
												Number.parseInt(e.target.value, 10) || 1,
											),
										})
									}
									disabled={saving}
								/>
							</label>
							<button
								type="button"
								className="tg-btn-danger inline-flex h-8 items-center rounded-lg px-2.5 text-[0.76rem] font-semibold"
								onClick={() =>
									onChange(overrides.filter((_, i) => i !== index))
								}
								disabled={saving}
							>
								Remove
							</button>
						</div>

						<div className="grid grid-cols-2 gap-2.5">
							<label className="grid gap-1">
								<span className="tg-text-primary text-[0.78rem] font-semibold">
									Sizing
								</span>
								<select
									className={smallSelectClassName}
									value={override.sizingMode}
									onChange={(e) =>
										patchRow(index, {
											sizingMode: e.target
												.value as TradingChannelTpSplitSizingMode,
										})
									}
									disabled={saving}
								>
									{Object.entries(
										TRADING_CHANNEL_TP_SPLIT_SIZING_MODE_DISPLAY,
									).map(([value, { label }]) => (
										<option key={value} value={value}>
											{label}
										</option>
									))}
								</select>
							</label>
							{override.sizingMode === "fixed-risk" && (
								<label className="grid gap-1">
									<span className="tg-text-primary text-[0.78rem] font-semibold">
										Fixed risk ($)
									</span>
									<input
										className={smallInputClassName}
										type="number"
										min="0"
										step="0.01"
										value={override.fixedRiskAmountPerTrade ?? ""}
										onChange={(e) =>
											patchRow(index, {
												fixedRiskAmountPerTrade: parseOptionalNumber(
													e.target.value,
												),
											})
										}
										disabled={saving}
									/>
								</label>
							)}
							{override.sizingMode === "risk-percent" && (
								<label className="grid gap-1">
									<span className="tg-text-primary text-[0.78rem] font-semibold">
										Risk (%)
									</span>
									<input
										className={smallInputClassName}
										type="number"
										min="0"
										step="0.05"
										value={override.percentageRiskPerTrade ?? ""}
										onChange={(e) =>
											patchRow(index, {
												percentageRiskPerTrade: parseOptionalNumber(
													e.target.value,
												),
											})
										}
										disabled={saving}
									/>
								</label>
							)}
							{override.sizingMode === "lot-size" && (
								<label className="grid gap-1">
									<span className="tg-text-primary text-[0.78rem] font-semibold">
										Lot size
									</span>
									<input
										className={smallInputClassName}
										type="number"
										min="0"
										step="0.01"
										value={override.lotSize ?? ""}
										onChange={(e) =>
											patchRow(index, {
												lotSize: parseOptionalNumber(e.target.value),
											})
										}
										disabled={saving}
									/>
								</label>
							)}
						</div>

						<div className="grid grid-cols-2 gap-2.5">
							<label className="grid gap-1">
								<span className="tg-text-primary text-[0.78rem] font-semibold">
									Auto-SL
								</span>
								<select
									className={smallSelectClassName}
									value={override.autoStopLossMode}
									onChange={(e) =>
										patchRow(index, {
											autoStopLossMode: e.target
												.value as TradingChannelTpSplitNumericMode,
										})
									}
									disabled={saving}
								>
									{Object.entries(
										TRADING_CHANNEL_TP_SPLIT_NUMERIC_MODE_DISPLAY,
									).map(([value, { label }]) => (
										<option key={value} value={value}>
											{label}
										</option>
									))}
								</select>
							</label>
							{override.autoStopLossMode === "value" && (
								<label className="grid gap-1">
									<span className="tg-text-primary text-[0.78rem] font-semibold">
										Auto-SL threshold (%)
									</span>
									<input
										className={smallInputClassName}
										type="number"
										min="0"
										step="1"
										value={override.autoStopLossThreshold ?? ""}
										onChange={(e) =>
											patchRow(index, {
												autoStopLossThreshold: parseOptionalNumber(
													e.target.value,
												),
											})
										}
										disabled={saving}
									/>
								</label>
							)}
						</div>

						<div className="grid grid-cols-2 gap-2.5">
							<label className="grid gap-1">
								<span className="tg-text-primary text-[0.78rem] font-semibold">
									Entry tolerance
								</span>
								<select
									className={smallSelectClassName}
									value={override.marketEntryToleranceMode}
									onChange={(e) =>
										patchRow(index, {
											marketEntryToleranceMode: e.target
												.value as TradingChannelTpSplitNumericMode,
										})
									}
									disabled={saving}
								>
									{Object.entries(
										TRADING_CHANNEL_TP_SPLIT_NUMERIC_MODE_DISPLAY,
									).map(([value, { label }]) => (
										<option key={value} value={value}>
											{label}
										</option>
									))}
								</select>
							</label>
							{override.marketEntryToleranceMode === "value" && (
								<label className="grid gap-1">
									<span className="tg-text-primary text-[0.78rem] font-semibold">
										Tolerance (%)
									</span>
									<input
										className={smallInputClassName}
										type="number"
										min="0"
										step="0.01"
										value={override.marketEntryTolerancePercent ?? ""}
										onChange={(e) =>
											patchRow(index, {
												marketEntryTolerancePercent: parseOptionalNumber(
													e.target.value,
												),
											})
										}
										disabled={saving}
									/>
								</label>
							)}
						</div>
					</div>
				))
			)}
		</div>
	);
};
